import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError, LOGO_URL } from "@/lib/api";
import { toast } from "sonner";
import GoogleSignInButton from "@/components/auth/GoogleSignInButton";

export default function Login() {
  const { login, siteSettings } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const res = await login(email, password);
      toast.success("Welcome back!");
      if (res.user.role === "admin") {
        navigate("/admin");
        return;
      }
      // Non-admins always land on the login-deal screen. That page fetches the
      // active deal and short-circuits to /dashboard when nothing is live, so
      // this is a safe single source of truth for whether users see a promo.
      navigate("/login-deal");
    } catch (err) {
      setError(formatApiError(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-blue-800 dark:bg-slate-950 flex flex-col" style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>
      <div className="p-4"><Link to="/"><img src={siteSettings?.logo_url || LOGO_URL} alt="logo" className="h-12" /></Link></div>
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="w-full max-w-md bg-white dark:bg-slate-900 rounded-3xl shadow-2xl p-8 border border-slate-200 dark:border-slate-800">
          <h1 className="text-3xl font-black text-slate-900 dark:text-white mb-1" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>Sign In</h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm mb-6">Welcome back to Start-A-TE</p>

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400">Email</label>
              <input data-testid="login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="w-full mt-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-4 py-3 outline-none focus:border-blue-500 dark:focus:border-blue-400 text-slate-900 dark:text-slate-100" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400">Password</label>
              <input data-testid="login-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="w-full mt-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-4 py-3 outline-none focus:border-blue-500 dark:focus:border-blue-400 text-slate-900 dark:text-slate-100" />
            </div>
            {error && <div data-testid="login-error" className="text-red-600 dark:text-red-400 text-sm">{error}</div>}
            <button data-testid="login-submit-btn" disabled={busy} className="w-full rounded-full bg-blue-700 hover:bg-blue-800 disabled:opacity-60 text-white font-bold py-3 transition-colors duration-200">
              {busy ? "Signing in…" : "Sign In"}
            </button>
          </form>

          {siteSettings?.google_signin_enabled && siteSettings?.google_client_id && (
            <>
              <div className="flex items-center gap-3 my-4" data-testid="login-google-divider">
                <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">or</div>
                <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
              </div>
              <div className="flex justify-center">
                <GoogleSignInButton clientId={siteSettings.google_client_id} mode="signin_with" />
              </div>
            </>
          )}

          <div className="mt-4 text-sm text-slate-600 dark:text-slate-300 flex justify-between">
            <Link data-testid="login-forgot-link" to="/forgot-password" className="hover:underline">Forgot password?</Link>
            <Link data-testid="login-register-link" to="/register" className="hover:underline">Create account</Link>
          </div>
        </div>
      </div>
      <div className="text-center pb-6 text-xs text-white/80">
        <Link data-testid="login-footer-terms-link" to="/terms" className="hover:underline">
          Terms of Service &amp; Privacy Policy
        </Link>
      </div>
    </div>
  );
}
